import { type TextInputProps } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { DigitRegisterInput } from './DigitRegisterInput';

export interface DurationSelectorProps extends Omit<TextInputProps, 'value' | 'defaultValue' | 'onChange' | 'type'> {
    defaultValue?: number | null;
    onChange?: (value: number | null) => void;
    onValidityChange?: (error: string | null) => void;
    withHours?: boolean;
    withFractionalSeconds?: boolean;
    clearable?: boolean;
}

const pad = (value: number, width: number): string => String(value).padStart(width, '0');

function getDurationMaxDigits(withHours: boolean, withFractionalSeconds: boolean): number {
    return (withHours ? 2 : 0) + 4 + (withFractionalSeconds ? 3 : 0);
}

function getDurationPlaceholder(withHours: boolean, withFractionalSeconds: boolean): string {
    return `${withHours ? 'HH:' : ''}MM:SS${withFractionalSeconds ? '.FFF' : ''}`;
}

function msToDurationDigits(ms: number, withHours: boolean, withFractionalSeconds: boolean): string {
    const positiveMs = Math.max(0, Math.trunc(ms));
    const totalSeconds = Math.floor(positiveMs / 1000);
    const milliseconds = positiveMs % 1000;
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = withHours ? totalMinutes % 60 : totalMinutes % 100;
    const hours = withHours ? Math.floor(totalMinutes / 60) % 100 : 0;

    const parts: string[] = [];
    if (withHours) {
        parts.push(pad(hours, 2));
    }
    parts.push(pad(minutes, 2), pad(seconds, 2));
    if (withFractionalSeconds) {
        parts.push(pad(milliseconds, 3));
    }
    const joined = parts.join('');
    return joined.replace(/^0+/, '') || '0';
}

function formatDurationDigits(digits: string, withHours: boolean, withFractionalSeconds: boolean): string {
    if (digits.length === 0) {
        return '';
    }
    const maxDigits = getDurationMaxDigits(withHours, withFractionalSeconds);
    const padded = digits.padStart(maxDigits, '0');

    let cursor = 0;
    let result = '';
    if (withHours) {
        result += padded.slice(cursor, (cursor += 2)) + ':';
    }
    result += padded.slice(cursor, (cursor += 2));
    result += ':' + padded.slice(cursor, (cursor += 2));
    if (withFractionalSeconds) {
        result += '.' + padded.slice(cursor, (cursor += 3));
    }
    return result;
}

function durationDigitsToMs(digits: string, withHours: boolean, withFractionalSeconds: boolean): number {
    const maxDigits = getDurationMaxDigits(withHours, withFractionalSeconds);
    const padded = digits.padStart(maxDigits, '0');

    let cursor = 0;
    const hours = withHours ? parseInt(padded.slice(cursor, (cursor += 2)), 10) : 0;
    const minutes = parseInt(padded.slice(cursor, (cursor += 2)), 10);
    const seconds = parseInt(padded.slice(cursor, (cursor += 2)), 10);
    const milliseconds = withFractionalSeconds ? parseInt(padded.slice(cursor, (cursor += 3)), 10) : 0;

    return ((hours * 60 + minutes) * 60 + seconds) * 1000 + milliseconds;
}

function getDurationValidationError(digits: string, withHours: boolean, withFractionalSeconds: boolean): string | null {
    if (digits.length === 0) {
        return null;
    }
    const maxDigits = getDurationMaxDigits(withHours, withFractionalSeconds);
    const padded = digits.padStart(maxDigits, '0');

    let cursor = withHours ? 2 : 0;
    const minutes = parseInt(padded.slice(cursor, cursor + 2), 10);
    cursor += 2;
    const seconds = parseInt(padded.slice(cursor, cursor + 2), 10);

    if (seconds > 59) {
        return 'Seconds must be less than 60';
    }
    if (withHours && minutes > 59) {
        return 'Minutes must be less than 60';
    }
    return null;
}

export const DurationSelector = ({
    defaultValue,
    onChange,
    onValidityChange,
    withHours = false,
    withFractionalSeconds = false,
    clearable = true,
    placeholder,
    error: parentError,
    ...props
}: DurationSelectorProps) => {
    const maxDigits = getDurationMaxDigits(withHours, withFractionalSeconds);
    const [digits, setDigits] = useState(() =>
        defaultValue != null ? msToDurationDigits(defaultValue, withHours, withFractionalSeconds) : ''
    );

    const validationError = getDurationValidationError(digits, withHours, withFractionalSeconds);

    const lastReportedValidityRef = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        if (lastReportedValidityRef.current === validationError) {
            return;
        }
        lastReportedValidityRef.current = validationError;
        onValidityChange?.(validationError);
    }, [validationError, onValidityChange]);

    const handleDigitsChange = (newDigits: string) => {
        setDigits(newDigits);
        const newError = getDurationValidationError(newDigits, withHours, withFractionalSeconds);
        const newValue =
            newError !== null || newDigits.length === 0
                ? null
                : durationDigitsToMs(newDigits, withHours, withFractionalSeconds);
        onChange?.(newValue);
    };

    // parent error takes precedence over the built-in validation error
    const finalError = parentError !== undefined ? parentError : validationError;

    return (
        <DigitRegisterInput
            digits={digits}
            onDigitsChange={handleDigitsChange}
            maxDigits={maxDigits}
            format={(d) => formatDurationDigits(d, withHours, withFractionalSeconds)}
            placeholder={placeholder ?? getDurationPlaceholder(withHours, withFractionalSeconds)}
            clearable={clearable}
            error={finalError}
            {...props}
        />
    );
};
